import { ApolloProvider } from "@apollo/client";
import { LightingConverger } from "components/global/components/LightingConverger";
import { LightingDebugOverlay } from "components/global/components/LightingDebugOverlay";
import { DeviceContextProvider } from "contexts/DeviceContext";
import { changeLanguage } from "i18next";
import { useEffect } from "react";
import { type EventsType, IdleTimerProvider, type PresenceType } from "react-idle-timer";
import { ConnectionErrorModal } from "./components/global/modals/ConnectionErrorModal";
import { useAppDispatch } from "./hooks/utilsHooks/storeHooks";
import { MealsListPage } from "./pages";
import { Config } from "./services/configuration/eatSmartConfig";
import "./services/i18n/config";
import { setAppState, setLocale } from "./slices/appStateSlice";
import type { AppState } from "./types/mainTypes";
import { useFridgeApolloClient } from "./utils/ApolloClientProvider";
import { getLocale } from "./utils/browser/localStorage";
import { debugTrace, shouldTraceLightingDebug } from "./utils/debug/debugTrace";

// react-idle-timer's default event list includes `visibilitychange` and `focus`, which fire
// constantly on iPadOS/WKWebView kiosk tablets (screen dim, Auto-Lock, Low Power Mode, app-switcher,
// Guided Access) — they were registering as "activity" and flapping `appState` ACTIVE↔IDLE, which
// blinked the fridge lights. Restrict activity detection to genuine user interaction so only real
// taps / scrolls / key input reset presence. Module-level constant = stable reference (no re-bind).
const IDLE_ACTIVITY_EVENTS: EventsType[] = ["touchstart", "touchmove", "mousedown", "keydown", "wheel"];

export const App = () => {
	const { client, isConnected, isSubscriptionBroken, setIsSubscriptionBroken } = useFridgeApolloClient();
	const dispatch = useAppDispatch();

	const locale = getLocale();

	useEffect(() => {
		setLocale(locale);
		changeLanguage(locale);
	}, [locale]);

	const onPresenceChange = (presence: PresenceType["type"]) => {
		dispatch(setAppState(presence as AppState));
	};

	return (
		<ApolloProvider client={client}>
			<IdleTimerProvider
				timeout={Config.activityMonitor.inactiveTimeout}
				events={IDLE_ACTIVITY_EVENTS}
				// TEMP diagnostic (master tablet only): if appState is still flapping on iPad, this names
				// the event that keeps registering as activity (throttled to react-idle-timer's
				// eventsThrottle). Pair with the "[state-debug] appState/setAppState" lines. Remove with
				// stateDebugMiddleware.
				onAction={(event) => {
					if (shouldTraceLightingDebug()) {
						debugTrace(`[state-debug] idle-action event=${event?.type ?? "?"}`);
					}
				}}
				onPresenceChange={(presence) => onPresenceChange(presence.type)}
			>
				<DeviceContextProvider
					client={client}
					appConnected={isConnected}
					isSubscriptionBroken={isSubscriptionBroken}
					setIsSubscriptionBroken={setIsSubscriptionBroken}
				>
					<LightingConverger />
					<MealsListPage />
					{shouldTraceLightingDebug() && <LightingDebugOverlay />}
					{!isConnected && (
						<ConnectionErrorModal appConnected={isConnected} isSubscriptionBroken={isSubscriptionBroken} />
					)}
				</DeviceContextProvider>
			</IdleTimerProvider>
		</ApolloProvider>
	);
};
